import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Brain, Loader2, Sparkles, CheckCircle,
  Edit3, Save, Bot, Plus, TrendingUp, TrendingDown, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const QUALITY_CONFIG = {
  good: { color: 'text-green-400', bg: 'bg-green-400/10', label: 'Good' },
  needs_improvement: { color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Needs Work' },
  poor: { color: 'text-red-400', bg: 'bg-red-400/10', label: 'Poor' },
};

export const AnalysisModal = ({ isOpen, onClose, api, subAccountId, phone, agents, isDark }) => {
  const [stage, setStage] = useState('idle');
  const [summary, setSummary] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [gameplan, setGameplan] = useState([]);
  const [conversationsAnalyzed, setConversationsAnalyzed] = useState(0);
  const [defaultAgentIds, setDefaultAgentIds] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editResponse, setEditResponse] = useState('');
  const [editAgentIds, setEditAgentIds] = useState([]);
  const [showAgentPicker, setShowAgentPicker] = useState(null);
  const [saving, setSaving] = useState(null);
  const [added, setAdded] = useState(new Set());

  const cardBg = isDark ? 'bg-[#0e1015]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/[0.06]' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDark ? 'bg-white/[0.04]' : 'bg-gray-50';

  const runAnalysis = async () => {
    setStage('loading');
    try {
      let res;
      if (phone) {
        res = await api.post(`/api/sub-accounts/${subAccountId}/contacts/${encodeURIComponent(phone)}/analyze`);
      } else {
        res = await api.post(`/api/sub-accounts/${subAccountId}/training/analyze`, { agent_ids: [] });
      }
      setSummary(res.data.summary || null);
      setQuestions(res.data.questions || []);
      setGameplan(res.data.gameplan || []);
      setConversationsAnalyzed(res.data.conversations_analyzed || 0);
      setDefaultAgentIds(res.data.agent_ids || agents.map(a => a.agent_id));
      setStage('results');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
      setStage('idle');
    }
  };

  const handleAddToTraining = async (q, idx) => {
    const response = editingIdx === idx ? editResponse : (q.suggestion && q.suggestion !== 'No change needed' ? q.suggestion : q.current_response);
    const agentIds = showAgentPicker === idx ? editAgentIds : defaultAgentIds;
    if (!response?.trim()) {
      toast.error('Write a trained response first');
      return;
    }
    setSaving(idx);
    try {
      await api.post(`/api/sub-accounts/${subAccountId}/training`, {
        question: q.question,
        trained_response: response,
        agent_ids: agentIds,
        current_response: q.current_response,
        quality: q.quality,
        frequency: q.frequency,
      });
      setAdded(prev => new Set([...prev, idx]));
      setEditingIdx(null);
      setShowAgentPicker(null);
      toast.success('Added to Training Hub');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
    setSaving(null);
  };

  const handleClose = () => {
    setStage('idle');
    setSummary(null);
    setQuestions([]);
    setGameplan([]);
    setAdded(new Set());
    setEditingIdx(null);
    setShowAgentPicker(null);
    onClose();
  };

  if (!isOpen) return null;

  const scoreColor = (s) => s >= 7 ? 'text-green-400' : s >= 4 ? 'text-amber-400' : 'text-red-400';

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
          className={`w-full max-w-2xl max-h-[85vh] ${isDark ? 'bg-[#0a0b10]' : 'bg-white'} rounded-2xl border ${cardBorder} shadow-2xl overflow-hidden flex flex-col`}
          onClick={e => e.stopPropagation()} data-testid="analysis-modal">

          {/* Header */}
          <div className={`px-6 py-4 border-b ${cardBorder} flex items-center justify-between shrink-0`}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-violet-400/15 to-teal-400/15">
                <Brain size={18} className="text-violet-400" />
              </div>
              <div>
                <h3 className={`text-base font-semibold ${textPrimary}`}>
                  {phone ? 'Contact Analysis' : 'Full Analysis'}
                </h3>
                <p className={`text-xs ${textSecondary}`}>
                  {stage === 'loading' ? 'Claude is analyzing conversations...'
                    : stage === 'results' ? `${conversationsAnalyzed} conversations analyzed`
                    : phone ? 'Analyze this contact\'s conversations' : 'Analyze all conversations'}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10 transition`} data-testid="close-analysis-modal">
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">

            {/* Idle */}
            {stage === 'idle' && (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-400/10 to-teal-400/10 flex items-center justify-center mb-4">
                  <Sparkles size={28} className="text-violet-400" />
                </div>
                <h4 className={`text-lg font-medium ${textPrimary} mb-2`}>Ready to Analyze</h4>
                <p className={`text-sm ${textSecondary} text-center mb-6 max-w-sm`}>
                  Claude will review {phone ? "this contact's" : 'all'} conversations — what's working, what needs improvement, and the questions leads ask.
                </p>
                <button onClick={runAnalysis}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-teal-500 text-white font-medium hover:opacity-90 transition"
                  data-testid="start-analysis-btn">
                  <Brain size={16} /> Start Analysis
                </button>
              </div>
            )}

            {/* Loading */}
            {stage === 'loading' && (
              <div className="flex flex-col items-center justify-center py-20 px-6">
                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-full border-2 border-violet-400/20 border-t-violet-400 animate-spin" />
                  <Brain size={20} className="text-violet-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <h4 className={`text-base font-medium ${textPrimary} mb-1`}>Analyzing Conversations</h4>
                <p className={`text-sm ${textSecondary}`}>This usually takes 10-20 seconds...</p>
              </div>
            )}

            {/* Results */}
            {stage === 'results' && (
              <div className="p-5 space-y-4" data-testid="analysis-results">

                {/* Summary Section */}
                {summary && (
                  <div className={`rounded-xl border ${cardBorder} overflow-hidden`} data-testid="analysis-summary">
                    {/* Score bar */}
                    <div className={`px-4 py-3 flex items-center justify-between ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                      <span className={`text-xs font-medium uppercase tracking-wider ${textSecondary}`}>Performance Summary</span>
                      {summary.overall_score && (
                        <div className="flex items-center gap-1.5">
                          <span className={`text-lg font-bold ${scoreColor(summary.overall_score)}`}>{summary.overall_score}</span>
                          <span className={`text-xs ${textSecondary}`}>/10</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 space-y-3">
                      {summary.whats_working && (
                        <div className="flex gap-2.5">
                          <TrendingUp size={14} className="text-green-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[10px] font-medium uppercase tracking-wider text-green-400 mb-0.5">What's Working</p>
                            <p className={`text-xs leading-relaxed ${textPrimary}`}>{summary.whats_working}</p>
                          </div>
                        </div>
                      )}
                      {summary.needs_improvement && (
                        <div className="flex gap-2.5">
                          <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[10px] font-medium uppercase tracking-wider text-amber-400 mb-0.5">Needs Improvement</p>
                            <p className={`text-xs leading-relaxed ${textPrimary}`}>{summary.needs_improvement}</p>
                          </div>
                        </div>
                      )}
                      {summary.conversion_feedback && (
                        <div className="flex gap-2.5">
                          <TrendingDown size={14} className="text-violet-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[10px] font-medium uppercase tracking-wider text-violet-400 mb-0.5">Conversion Insights</p>
                            <p className={`text-xs leading-relaxed ${textPrimary}`}>{summary.conversion_feedback}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Gameplan Section */}
                {gameplan.length > 0 && (
                  <div className={`rounded-xl border ${cardBorder} overflow-hidden`} data-testid="analysis-gameplan">
                    <div className={`px-4 py-2.5 ${isDark ? 'bg-teal-400/5' : 'bg-teal-50'}`}>
                      <span className="text-[10px] font-medium uppercase tracking-wider text-teal-400">Gameplan to Improve</span>
                    </div>
                    <div className="p-4 space-y-2.5">
                      {gameplan.map((item, i) => (
                        <div key={i} className="flex gap-2.5">
                          <span className={`text-xs font-bold text-teal-400 mt-0.5 shrink-0`}>{i + 1}.</span>
                          <p className={`text-xs leading-relaxed ${textPrimary}`}>{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Questions Header */}
                {questions.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium uppercase tracking-wider ${textSecondary}`}>Questions Leads Ask</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.05] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                      {questions.length}
                    </span>
                  </div>
                )}

                {/* Question Cards */}
                {questions.length === 0 && !summary ? (
                  <div className="text-center py-12">
                    <p className={textSecondary}>No patterns detected. Try with more conversations.</p>
                  </div>
                ) : questions.map((q, idx) => {
                  const qc = QUALITY_CONFIG[q.quality] || QUALITY_CONFIG.needs_improvement;
                  const isAdded = added.has(idx);
                  const isEditing = editingIdx === idx;
                  const isPickingAgents = showAgentPicker === idx;

                  return (
                    <div key={idx} className={`${cardBg} rounded-xl border ${cardBorder} p-4 ${isAdded ? 'opacity-40' : ''}`}
                      data-testid={`analysis-question-${idx}`}>

                      {/* Question + quality */}
                      <div className="flex items-start gap-2 mb-2">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${qc.color.replace('text-', 'bg-')}`} />
                        <span className={`text-sm font-medium ${textPrimary} flex-1`}>{q.question}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${qc.bg} ${qc.color}`}>{qc.label}</span>
                        <span className={`text-[10px] ${textSecondary} shrink-0`}>{q.frequency}x</span>
                      </div>

                      {/* Current AI response */}
                      <div className={`rounded-lg p-2.5 mb-2 ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                        <p className={`text-[10px] uppercase tracking-wider ${textSecondary} mb-0.5`}>AI responds</p>
                        <p className={`text-xs ${textPrimary} leading-relaxed`}>{q.current_response}</p>
                      </div>

                      {/* Suggestion */}
                      {q.suggestion && q.suggestion !== 'No change needed' && !isEditing && (
                        <p className={`text-xs text-amber-400/80 mb-2`}>{q.suggestion}</p>
                      )}

                      {/* Edit form */}
                      {isEditing && (
                        <div className="mb-2">
                          <textarea value={editResponse} onChange={e => setEditResponse(e.target.value)} rows={2}
                            className={`w-full rounded-lg border ${cardBorder} ${inputBg} ${textPrimary} p-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400/40 resize-none`}
                            placeholder="Write the ideal response..."
                            data-testid={`edit-response-${idx}`} />
                        </div>
                      )}

                      {/* Agent picker */}
                      {isPickingAgents && (
                        <div className="mb-2">
                          <p className={`text-[10px] uppercase tracking-wider ${textSecondary} mb-1.5`}>Agents with access</p>
                          <div className="flex flex-wrap gap-1.5">
                            {agents.map(a => {
                              const sel = editAgentIds.includes(a.agent_id);
                              return (
                                <button key={a.agent_id}
                                  onClick={() => setEditAgentIds(prev => sel ? prev.filter(id => id !== a.agent_id) : [...prev, a.agent_id])}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] border transition ${sel
                                    ? 'bg-teal-400/10 border-teal-400/30 text-teal-400'
                                    : `${isDark ? 'bg-white/[0.02] border-white/[0.06] text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'} hover:border-teal-400/20`
                                  }`}
                                  data-testid={`pick-agent-${a.agent_id}-${idx}`}>
                                  <Bot size={10} /> {a.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {!isAdded && (
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6' }}>
                          {!isEditing ? (
                            <button onClick={() => {
                              setEditingIdx(idx);
                              setEditResponse(q.suggestion && q.suggestion !== 'No change needed' ? q.suggestion : q.current_response);
                              setShowAgentPicker(idx);
                              setEditAgentIds([...defaultAgentIds]);
                            }}
                              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs ${isDark ? 'bg-white/[0.04] text-gray-300 hover:bg-white/[0.08]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} transition`}
                              data-testid={`edit-btn-${idx}`}>
                              <Edit3 size={11} /> Edit & Train
                            </button>
                          ) : (
                            <button onClick={() => { setEditingIdx(null); setShowAgentPicker(null); }}
                              className={`px-2.5 py-1.5 rounded-lg text-xs ${isDark ? 'bg-white/[0.04] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                              Cancel
                            </button>
                          )}
                          <div className="flex-1" />
                          <button onClick={() => handleAddToTraining(q, idx)} disabled={saving === idx}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500 text-white text-xs font-medium hover:bg-teal-600 disabled:opacity-50 transition"
                            data-testid={`add-training-btn-${idx}`}>
                            {saving === idx ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                            Add to Training
                          </button>
                        </div>
                      )}

                      {isAdded && (
                        <div className="flex items-center gap-1.5 mt-2 pt-2 text-green-400" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6'}` }}>
                          <CheckCircle size={13} /> <span className="text-xs font-medium">Added to Training Hub</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
